import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import { AtSymbolIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline'
import { Controller, useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import _ from 'lodash'
import { CircleDashed } from 'tabler-icons-react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

// Components
import { Button } from '../../components/Button'
import { DataTable } from '../../components/DataTable'
import { EventHeader } from '../../components/EventHeader'
import { FileUploader } from '../../components/FileUploader'
import { Modal } from '../../components/Modal'
import { RichTextInput } from '../../components/RichTextInput'
import { StateContainer } from '../../components/StateContainer'
import { TextInput } from '../../components/TextInput'
import PreviewEmailModal from './PreviewEmailModal'

// Store
import { NavigationStoreContext } from '../../stores/NavigationStore'

// Service
import {
  getEventEmails,
  sendEmail,
  updateEvent,
  updateEventEmail,
} from '../../services/events.service'

// Utils
import colors from '../../utils/colors'
import { handlePagination, toast } from '../../utils/helpers'
import { BASE_RECIPIENT_FIELDS, CONTEXT_MERGE_FIELDS } from '../../utils/constants'

/**
 *
 * EventEmails
 *
 */
const EventEmails = observer(() => {
  // Context
  const { event, setEvent } = useContext(NavigationStoreContext)

  // State
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [emails, setEmails] = useState([])
  const [editEmail, setEditEmail] = useState(null)
  const [loadingEmail, setLoadingEmail] = useState(null)
  const [loadingSend, setLoadingSend] = useState(null)
  const [pendingEmails, setPendingEmails] = useState([])
  const [emailPayload, setEmailPayload] = useState({
    type: null,
    data: null,
  })
  const [previewEmail, setPreviewEmail] = useState(null)

  // Header
  const [fileLoaded, setFileLoaded] = useState(event?.signedEmailHeaderImage)
  const [uploadHeader, setUploadHeader] = useState(!event?.signedEmailHeaderImage)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [pages, setPages] = useState(null)

  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')

  /**
   * Gets updated emails for this event using the specified `url`.
   */
  const getUpdatedEmails = async (url) => {
    const response = await getEventEmails(url, setError, setLoading, () => {})

    if (response) {
      setEmails(response.results)
      setTotalRows(response.count)
      setPages(response.paperPages)
    }
  }

  useEffect(() => {
    if (event) {
      getUpdatedEmails(`/events/${event.id}/emails/?limit=${perPage}&page=${currentPage}`)
    }
  }, [])

  /**
   * Returns messaging describing who will receive email based on the email's `recipients` setting.
   */
  const renderRecipientsText = () => {
    if (emailPayload.data?.recipients === 'Licensees (Dynamic)')
      return 'all Exhibitors who are assigned a license'
    if (emailPayload.data?.recipients === 'Attendees (Dynamic)') return 'all Attendees'
    if (emailPayload.data?.recipients === 'Exhibitor Admin (Dynamic)')
      return 'all Exhibitor admins'
    return 'recipients'
  }

  /**
   * Renders an abbreviated version of specified `title`.
   * @param {string} title
   */
  const renderShortEmailTitle = (title) => {
    if (title === 'Fetch - KBYG for Licensees (Invitation or Get Started)') return 'KBYG'
    return 'Scan and Go'
  }

  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    reset,
    setError: setFormError,
  } = useForm({
    defaultValues: {
      suject: null,
      body: null,
    },
  })

  /**
   * Handles submitting the update email form.
   * @param {object} data
   */
  const onSubmit = async (data) => {
    if (data.body === null || data.body === '<p><br></p>') {
      setFormError('body')
      return
    }

    const response = await updateEventEmail(
      event.id,
      { id: editEmail.id, subject: data.subject, body: data.body },
      handleError,
      setLoadingEmail,
      handleSuccess,
    )

    if (response) {
      getUpdatedEmails(`/events/${event.id}/emails/?limit=${perPage}&page=${currentPage}`)

      setEditEmail(null)
      reset()
    }
  }

  /**
   * Renders the send button for the specified `data` and `label`.
   * @param {object} data
   * @param {string} label
   */
  const renderSendButton = (data, label) => (
    <button
      disabled={loadingSend && loadingSend === data.id}
      type="button"
      className={mergeClassNames(
        'px-2 py-0.5 font-bold text-blue-600 hover:rounded-full hover:bg-status-blue disabled:opacity-50',
        label === 'Resend' && 'text-purple-600 hover:bg-status-purple',
      )}
      onClick={async () => {
        setEmailPayload({
          type: label,
          data,
        })
      }}
    >
      <div className="flex flex-row items-center">
        {loadingSend && loadingSend === data.id && (
          <svg className="mr-1 h-4 w-4 motion-safe:animate-spin-slow" viewBox="0 0 24 24">
            <CircleDashed
              size={24}
              strokeWidth={2}
              color={label === 'Resend' ? colors.purple.DEFAULT : colors.blue.DEFAULT}
            />
          </svg>
        )}
        {label}
      </div>
    </button>
  )

  const renderLoadingOrError = () => {
    if (loading) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center space-y-2">
          <span className="text-2xl font-bold">Loading...</span>

          <span className="flex items-center pr-3">
            <div className="h-10 w-10">
              {/* eslint-disable-next-line tailwindcss/no-custom-classname, tailwindcss/classnames-order */}
              <svg className="h-10 w-10 motion-safe:animate-spin-slow" viewBox="0 0 40 40">
                <ArrowPathIcon className="h-10 w-10" aria-hidden="true" />
              </svg>
            </div>
          </span>
        </div>
      )
    }

    if (error) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center space-y-2">
          <span className="text-lg font-bold text-red">Error generating report.</span>
        </div>
      )
    }

    return null
  }

  return (
    <div className="h-full w-full">
      <StateContainer>
        <div className="h-full w-full flex-col space-y-3 overflow-y-auto p-3">
          <div className="mb-3 flex flex-col items-start justify-between space-y-1 sm:flex-row sm:items-center sm:space-y-0">
            <EventHeader event={event} />
          </div>

          <div className="mt-1.5 flex flex-col">
            <h2 className="text-xl font-bold text-dark">Customize Emails</h2>

            <span className="text-md mt-6 font-bold">Emails</span>
            <span className="mb-2 text-xs">
              Manage emails that will be sent to Exhibitors and Attendees for this event.
            </span>

            {emails && !loading ? (
              <DataTable
                columns={[
                  {
                    id: 'name',
                    grow: 1,
                    name: 'Name',
                    selector: (row) => row.name,
                    cell: (row) => <span className="text-sm font-bold text-dark">{row.name}</span>,
                    minWidth: '280px',
                  },
                  {
                    id: 'type',
                    grow: 0.2,
                    name: 'Type',
                    selector: (row) => row.trigger,
                    cell: (row) => {
                      if (row.trigger === 'Manual - EE Triggered') {
                        return (
                          <div className="rounded-full bg-status-blue px-2.5 py-0.5">
                            <span className="text-xs font-bold text-status-darkBlue">Manual</span>
                          </div>
                        )
                      }
                      if (row.trigger === 'Disabled') {
                        return (
                          <div className="rounded-full bg-gray-300 px-2.5 py-0.5">
                            <span className="text-xs font-bold text-dark">Disabled</span>
                          </div>
                        )
                      }

                      return (
                        <div className="rounded-full bg-status-purple px-2.5 py-0.5">
                          <span className="text-xs font-bold text-role-user">Automatic</span>
                        </div>
                      )
                    },
                    center: true,
                    minWidth: '120px',
                  },
                  {
                    id: 'sentAt',
                    grow: 0.5,
                    name: 'Date Sent',
                    selector: (row) => row.sentAt,
                    cell: (row) => {
                      if (row.trigger !== 'Manual - EE Triggered') {
                        return 'N/A'
                      }

                      if (row.sentAt)
                        return (
                          <div className="flex shrink-0 flex-row place-items-center space-x-2">
                            <span>{dayjs(row.sentAt).format('MM/DD/YYYY HH:mm A')}</span>

                            {renderSendButton(row, 'Resend')}
                          </div>
                        )

                      return pendingEmails.includes(row.id) ? (
                        <div className="rounded-full bg-success-light px-2.5 py-0.5">
                          <span className="text-xs font-bold text-success">Pending</span>
                        </div>
                      ) : (
                        renderSendButton(row, 'Send')
                      )
                    },
                    center: true,
                    minWidth: '200px',
                  },
                  {
                    id: 'edit',
                    grow: 0.2,
                    name: '',
                    cell: (row) => (
                      <button
                        className="font-bold text-blue-600 hover:rounded-full hover:bg-status-blue hover:px-2 hover:py-0.5"
                        type="button"
                        onClick={() => {
                          reset(row)
                          setEditEmail(row)
                        }}
                      >
                        Edit
                      </button>
                    ),
                    center: true,
                    width: '100px',
                  },
                  {
                    id: 'preview',
                    grow: 0.2,
                    name: '',
                    cell: (row) => (
                      <button
                        className="font-bold text-blue-600 hover:rounded-full hover:bg-status-blue hover:px-2 hover:py-0.5"
                        type="button"
                        onClick={() => setPreviewEmail(row)}
                      >
                        Preview
                      </button>
                    ),
                    center: true,
                    minWidth: '110px',
                  },
                ]}
                data={emails}
                onChangePage={(page) =>
                  handlePagination(
                    page,
                    currentPage,
                    perPage,
                    totalRows,
                    pages,
                    setCurrentPage,
                    getUpdatedEmails,
                    `/events/${event.id}/emails/?limit=`,
                  )
                }
                onChangeRowsPerPage={async (currentRowsPerPage) => setPerPage(currentRowsPerPage)}
                pagination
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[10, 15, 20, 30, 50]}
                paginationTotalRows={totalRows}
                paginationServer
                progressPending={loading}
              />
            ) : (
              renderLoadingOrError()
            )}

            <span className="text-md font-bold">Event Header</span>
            <span className="mb-2 text-xs">
              Header image for Attendee emails (1200px x 110px). Default header will be Eventstack
              branding.
            </span>

            {event?.signedEmailHeaderImage && !uploadHeader ? (
              <div className="space-y-2">
                <Button
                  className="text-xs"
                  label="Replace"
                  onClick={() => {
                    setUploadHeader(true)
                    setFileLoaded(true)
                  }}
                />

                <img className="w-full" src={event.signedEmailHeaderImage} alt="Email Header" />
              </div>
            ) : (
              uploadHeader && (
                <FileUploader
                  acceptedFileTypes={['image/*']}
                  allowResize
                  allowRevert
                  fileLoaded={fileLoaded}
                  handleUploadToServer={async (file) => {
                    setUploadHeader(false)
                    const updatedEvent = await updateEvent(
                      { id: event.id, emailHeaderImage: file.url },
                      handleError,
                      () => {},
                      handleSuccess,
                    )

                    setEvent(updatedEvent)
                  }}
                  isPublic
                  id="emailHeaderImage"
                  imageCropAspectRatio="1:1"
                  setFileLoaded={(loaded) => {
                    if (event.signedEmailHeaderImage && !loaded) {
                      setUploadHeader(false)
                    } else {
                      setFileLoaded(loaded)
                    }
                  }}
                  type="gcp"
                  whiteBackground
                />
              )
            )}
          </div>
        </div>
      </StateContainer>

      <Modal
        actions={[
          {
            type: 'cancel',
            label: 'Cancel',
            onClick: () => setEditEmail(null),
          },
          {
            type: 'submit',
            label: 'Update Email',
            onClick: handleSubmit(onSubmit),
          },
        ]}
        icon={<AtSymbolIcon className="h-8 stroke-white" />}
        containerClassName="h-[90%]"
        content={
          <div className="flex h-full w-full flex-row space-x-4 py-3 text-center sm:py-5">
            <div className="flex w-3/4 flex-col space-y-4 overflow-scroll pb-4">
              <TextInput
                className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                data-testid="subject"
                disabled={loadingEmail}
                error={errors.subject && 'This field is required'}
                fullWidth
                id="subject"
                inputStyles="rounded-none"
                name="subject"
                nunito
                label="Subject"
                placeholder="Subject"
                {...register('subject', { required: true })}
              />

              <div className="flex flex-col justify-start tall:h-[90%] short:h-[80%]">
                <label
                  htmlFor="body"
                  className="w-fit pb-1 font-nunito text-sm font-medium text-gray-700"
                >
                  Body
                </label>

                <Controller
                  name="body"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <div className="tall:h-[95%] short:h-[90%]">
                      <RichTextInput
                        className="sm:h-full"
                        historyEnabled
                        id="body"
                        inputStyles="tall:h-[95%] short:h-[90%]"
                        disabled={loadingEmail}
                        onChange={onChange}
                        placeholder="Body"
                        value={value}
                      />

                      {errors.body && (
                        <div className="mt-1 w-full bg-error-light px-2 py-1">
                          <p className="text-sm font-medium text-error-dark" id="error-rte">
                            This field is required
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="h-[95%] w-[1px] bg-black" />

            <div className="flex h-full w-1/4 flex-col overflow-scroll pb-4">
              <span className="w-fit pb-1 font-nunito text-sm font-medium text-gray-700">
                Merge Fields
              </span>

              {editEmail?.availableContext?.length > 0 ? (
                _.map(
                  _.concat(
                    BASE_RECIPIENT_FIELDS,
                    _.flatMap(editEmail?.availableContext, (c) => CONTEXT_MERGE_FIELDS[c]),
                  ),
                  (f) => (
                    <button
                      className="group flex cursor-pointer flex-row"
                      key={`${f}`}
                      onClick={() => {
                        navigator.clipboard.writeText(`{{ ${f} }}`)
                        handleSuccess('Merge field copied to clipboard!')
                      }}
                      type="button"
                    >
                      <span className="group-hover:text-purple">{f}</span>

                      <ClipboardDocumentListIcon className="hidden h-6 group-hover:flex group-hover:stroke-black" />
                    </button>
                  ),
                )
              ) : (
                <span className="self-start text-sm text-gray-800">
                  No Merge Fields Available.
                </span>
              )}
            </div>
          </div>
        }
        className="sm:h-[90vh] sm:max-h-[1200px] sm:w-[90vw] sm:max-w-screen-xl"
        loading={loadingEmail}
        open={!!editEmail}
        title="Update Email"
      />

      <Modal
        actions={[
          {
            type: 'cancel',
            label: 'Cancel',
            onClick: () => {
              setEmailPayload({
                type: null,
                data: null,
              })
            },
          },
          {
            type: 'submit',
            label: `${emailPayload.type} ${renderShortEmailTitle(emailPayload.data?.name)}`,
            onClick: async () => {
              setLoadingSend(emailPayload.data?.id)
              setPendingEmails([...pendingEmails, emailPayload.data?.id])

              await sendEmail(
                event.id,
                emailPayload.data?.id,
                emailPayload.type === 'Resend' ? { forceResend: true } : null,
                (e) => {
                  handleError(e)
                  setLoadingSend(null)
                },
                () => {},
                (m) => {
                  handleSuccess(m)
                  getUpdatedEmails(`/events/${event.id}/emails/?limit=${perPage}&page=1`)
                  setLoadingSend(null)
                },
              )

              setEmailPayload({
                type: null,
                data: null,
              })
            },
          },
        ]}
        icon={<AtSymbolIcon className="h-8 stroke-white" />}
        content={
          <div className="mt-3 flex flex-col space-y-4 sm:mt-5">
            <div className="flex flex-col justify-center space-y-2">
              This email will be sent to {renderRecipientsText()} and then will be automatically
              sent to any added moving forward.
            </div>
          </div>
        }
        loading={loadingSend}
        open={emailPayload.data}
        title="Are you sure?"
      />

      {previewEmail && (
        <PreviewEmailModal
          email={previewEmail}
          eventId={event.id}
          onClose={() => setPreviewEmail(null)}
        />
      )}
    </div>
  )
})

export default EventEmails
